/* ============================================================
   9MM ENERGY SYSTEM — kinetic behaviours
   Injects the living atmosphere, scroll progress, section glow,
   hero scroll-cue and subtle GSAP scroll parallax.
   Purely additive — no existing markup required.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(tag, cls) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    return n;
  }

  function build() {
    var body = document.body;
    if (!body) return;

    /* --- Living atmosphere (drifting orbs behind everything) --- */
    if (!document.querySelector('.nm-atmos')) {
      var atmos = el('div', 'nm-atmos');
      atmos.setAttribute('aria-hidden', 'true');
      atmos.appendChild(el('div', 'nm-atmos__orb nm-atmos__orb--red'));
      atmos.appendChild(el('div', 'nm-atmos__orb nm-atmos__orb--cyan'));
      atmos.appendChild(el('div', 'nm-atmos__orb nm-atmos__orb--violet'));
      body.appendChild(atmos);
    }

    /* --- Film grain overlay --- */
    if (!document.querySelector('.nm-grain')) {
      var grain = el('div', 'nm-grain');
      grain.setAttribute('aria-hidden', 'true');
      body.appendChild(grain);
    }

    /* --- Scroll progress bar --- */
    var bar = document.querySelector('.nm-scroll-progress');
    if (!bar) {
      bar = el('div', 'nm-scroll-progress');
      bar.setAttribute('aria-hidden', 'true');
      body.appendChild(bar);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var h = document.documentElement;
        var max = (h.scrollHeight - h.clientHeight) || 1;
        var p = Math.min(1, Math.max(0, (window.scrollY || h.scrollTop) / max));
        bar.style.transform = 'scaleX(' + p + ')';
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();

    /* --- Alternating section light-pools (.is-lit when in view) --- */
    var sections = document.querySelectorAll('.nm-section');
    if (sections.length && 'IntersectionObserver' in window) {
      var lit = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) e.target.classList.add('is-lit');
        });
      }, { threshold: 0.15 });
      sections.forEach(function (s) { lit.observe(s); });
    } else {
      sections.forEach(function (s) { s.classList.add('is-lit'); });
    }

    /* --- Hero scroll cue --- */
    var hero = document.querySelector('.nm-hero-slider') || document.querySelector('.nm-hero');
    if (hero && !hero.querySelector('.nm-scroll-cue')) {
      var cue = el('div', 'nm-scroll-cue');
      cue.setAttribute('aria-hidden', 'true');
      cue.innerHTML = '<span class="nm-scroll-cue__mouse"></span><span>Scroll</span>';
      hero.appendChild(cue);
    }

    /* --- GSAP scroll parallax (subtle, desktop only) --- */
    if (!reduce && window.gsap && window.ScrollTrigger && window.innerWidth > 768) {
      var gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);

      // Atmosphere orbs drift slightly with scroll for depth
      var orbRed = document.querySelector('.nm-atmos__orb--red');
      var orbCyan = document.querySelector('.nm-atmos__orb--cyan');
      if (orbRed) {
        gsap.to(orbRed, { yPercent: 18, ease: 'none',
          scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 1.2 } });
      }
      if (orbCyan) {
        gsap.to(orbCyan, { yPercent: -18, ease: 'none',
          scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 1.2 } });
      }

      // Magnetic buttons — subtle pull toward the cursor
      document.querySelectorAll('.nm-btn').forEach(function (btn) {
        var strength = 0.35;
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          var mx = e.clientX - r.left - r.width / 2;
          var my = e.clientY - r.top - r.height / 2;
          gsap.to(btn, { x: mx * strength, y: my * strength, duration: 0.4, ease: 'power3.out' });
        });
        btn.addEventListener('mouseleave', function () {
          gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
        });
      });
    }

    /* --- SIGNATURE: Red/Cyan world sync + flip bloom --- */
    initWorldSync();

    /* --- Lenis smooth scroll (desktop wheel; native touch on mobile) --- */
    initLenis();

    /* --- Energy cursor states --- */
    initCursorStates();

    /* --- Fly-to-cart on add --- */
    initFlyToCart();

    /* --- Collection cards: per-product accent + quick-add feedback --- */
    initCollectionCards();

    /* --- Page exit wipe on internal navigation --- */
    initPageWipe();

    /* --- Easter egg: 9 clicks on the logo (calibre 9) --- */
    initEasterEgg();

    /* --- Easter eggs: console hint, "9MM" keystroke surge, cart 9/9 --- */
    initEasterEggsExtra();

    /* --- Opt-in metallic UI sounds (OFF by default) --- */
    initSoundFX();
  }

  /* ==========================================================
     SOUND FX — synthesized WebAudio clicks, strictly opt-in.
     No audio assets, no autoplay: the context is only created
     after the user flips the toggle (a user gesture).
     ========================================================== */
  function initSoundFX() {
    var css = document.createElement('style');
    css.textContent =
      '.nm-sfx-toggle{position:fixed;left:1.1rem;bottom:5.4rem;z-index:9500;width:40px;height:40px;border-radius:50%;' +
      'display:flex;align-items:center;justify-content:center;cursor:pointer;border:1px solid rgba(255,255,255,.18);' +
      'background:rgba(8,8,16,.72);color:rgba(255,255,255,.55);transition:color .25s ease,border-color .25s ease,transform .25s cubic-bezier(.16,1,.3,1);}' +
      '.nm-sfx-toggle:hover{transform:translateY(-2px);}' +
      '.nm-sfx-toggle.is-on{color:var(--bleu-esprit,#00edef);border-color:rgba(0,237,239,.6);box-shadow:0 0 14px rgba(0,237,239,.25);}' +
      '[data-theme="light"] .nm-sfx-toggle{background:rgba(255,255,255,.85);color:rgba(10,10,18,.55);border-color:rgba(0,0,0,.12);}' +
      '[data-theme="light"] .nm-sfx-toggle.is-on{color:#0097a7;border-color:rgba(0,151,167,.5);}' +
      '@media (max-width:768px){.nm-sfx-toggle{display:none;}}'; // desktop refinement only
    document.head.appendChild(css);

    var btn = el('button', 'nm-sfx-toggle');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Sons de l’interface');
    btn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';
    document.body.appendChild(btn);

    var ctx = null;
    var on = localStorage.getItem('9mm-sfx') === 'on';
    btn.classList.toggle('is-on', on);

    function ac() {
      if (!ctx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
      }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }

    /* short metallic tick: tiny square blip + filtered noise snap */
    function tick(freq, gainMax, dur) {
      var c = ac();
      if (!c) return;
      var t = c.currentTime;
      var osc = c.createOscillator();
      osc.type = 'square';
      osc.frequency.value = freq;
      var g = c.createGain();
      g.gain.setValueAtTime(gainMax, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(g).connect(c.destination);
      osc.start(t); osc.stop(t + dur);
    }
    function click() { tick(1900, 0.05, 0.045); }
    function kachink() { tick(2300, 0.06, 0.05); setTimeout(function () { tick(3200, 0.05, 0.07); }, 65); }

    btn.addEventListener('click', function () {
      on = !on;
      localStorage.setItem('9mm-sfx', on ? 'on' : 'off');
      btn.classList.toggle('is-on', on);
      if (on) kachink(); // audible confirmation (inside the gesture)
    });

    document.addEventListener('click', function (e) {
      if (!on || !e.target.closest) return;
      if (e.target.closest('.nm-sfx-toggle')) return;
      var f = e.target.closest('form[action*="/cart/add"]');
      if (f || e.target.closest('#nm-sticky-atc-btn')) { kachink(); return; }
      if (e.target.closest('.nm-btn, a.nm-logo, .nm-hs-tab, .nm-header a')) click();
    }, true);
  }

  /* ==========================================================
     EASTER EGGS EXTRA — a little treasure hunt chain:
     console signature → type "9MM" → 9 logo clicks → CALIBRE9.
     ========================================================== */
  function initEasterEggsExtra() {
    var css = document.createElement('style');
    css.textContent =
      '.nm-toast{position:fixed;left:50%;bottom:2.2rem;transform:translate(-50%,20px);z-index:12500;' +
      'padding:.85rem 1.6rem;border-radius:12px;background:rgba(8,8,16,.92);color:#fff;' +
      'font-family:var(--font-display,sans-serif);font-size:.9rem;letter-spacing:.14em;text-transform:uppercase;' +
      'border:1px solid rgba(255,255,255,.12);box-shadow:0 0 24px rgba(255,23,23,.35),0 0 48px rgba(0,237,239,.25);' +
      'opacity:0;pointer-events:none;transition:opacity .35s ease,transform .35s cubic-bezier(.16,1,.3,1);white-space:nowrap;}' +
      '.nm-toast.is-on{opacity:1;transform:translate(-50%,0);}' +
      '.nm-toast em{font-style:normal;color:var(--bleu-esprit,#00edef);}' +
      '.nm-toast strong{font-weight:inherit;color:var(--rouge-corps,#ff1717);}';
    document.head.appendChild(css);

    var toastEl = null, toastT = null;
    function toast(html, ms) {
      if (!toastEl) { toastEl = el('div', 'nm-toast'); document.body.appendChild(toastEl); }
      toastEl.innerHTML = html;
      requestAnimationFrame(function () { toastEl.classList.add('is-on'); });
      clearTimeout(toastT);
      toastT = setTimeout(function () { toastEl.classList.remove('is-on'); }, ms || 2600);
    }

    /* 1. Console signature — first link of the chain */
    try {
      console.log(
        '%c 9MM %c BEYOND ENERGY ',
        'background:#ff1717;color:#fff;font-weight:bold;padding:4px 8px;border-radius:4px 0 0 4px;font-size:14px;',
        'background:#00edef;color:#080810;font-weight:bold;padding:4px 8px;border-radius:0 4px 4px 0;font-size:14px;'
      );
      console.log('%cTu regardes sous le capot ? Respect.\nTape « 9MM » n’importe où sur le site… et le logo cache un secret à neuf coups.',
        'color:#8a8a96;font-size:12px;line-height:1.6;');
    } catch (e) {}

    /* 2. Type "9MM" anywhere (outside form fields) → energy surge:
          flash + world flip Body↔Mind. The MutationObserver in
          initWorldSync picks up the data-mode change and blooms. */
    var buf = '';
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      if (!e.key || e.key.length !== 1) return;
      buf = (buf + e.key.toLowerCase()).slice(-3);
      if (buf !== '9mm') return;
      buf = '';
      var flash = el('div', 'nm-egg-flash');
      document.body.appendChild(flash);
      setTimeout(function () { flash.remove(); }, 1000);
      var next = document.body.getAttribute('data-mode') === 'mind' ? 'body' : 'mind';
      document.body.setAttribute('data-mode', next);
      toast(next === 'mind'
        ? 'OVERDRIVE — UNIVERS <em>ACTIVE MIND</em>'
        : 'OVERDRIVE — UNIVERS <strong>ACTIVE BODY</strong>');
    });

    /* 3. Exactly 9 items in the cart → perfect calibre (once per session) */
    var count = document.getElementById('nm-drawer-count');
    if (count && !sessionStorage.getItem('nm-egg-9')) {
      var mo = new MutationObserver(function () {
        var n = parseInt((count.textContent || '').replace(/\D/g, ''), 10);
        if (n === 9) {
          sessionStorage.setItem('nm-egg-9', '1');
          mo.disconnect();
          toast('<strong>CALIBRE PARFAIT</strong> — 9 sur 9 ✦', 3200);
        }
      });
      mo.observe(count, { childList: true, characterData: true, subtree: true });
    }

    /* 4. Konami code → rain of Body/Mind capsules */
    var KONAMI = ['arrowup','arrowup','arrowdown','arrowdown','arrowleft','arrowright','arrowleft','arrowright','b','a'];
    var kPos = 0;
    var rainCss = document.createElement('style');
    rainCss.textContent =
      '@keyframes nmRainFall{0%{transform:translateY(-6vh) rotate(0deg);opacity:1}' +
      '90%{opacity:1}100%{transform:translateY(112vh) rotate(340deg);opacity:0}}' +
      '.nm-rain-p{position:fixed;top:0;width:11px;height:24px;border-radius:6px;z-index:12400;pointer-events:none;' +
      'animation:nmRainFall linear forwards;}';
    document.head.appendChild(rainCss);
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      var k = (e.key || '').toLowerCase();
      kPos = (k === KONAMI[kPos]) ? kPos + 1 : (k === KONAMI[0] ? 1 : 0);
      if (kPos < KONAMI.length) return;
      kPos = 0;
      for (var i = 0; i < 45; i++) {
        var p = el('div', 'nm-rain-p');
        var red = i % 2 === 0;
        p.style.left = (Math.random() * 100) + 'vw';
        p.style.background = red
          ? 'linear-gradient(180deg,#ff1717,#7a0b0b)'
          : 'linear-gradient(180deg,#00edef,#00767a)';
        p.style.boxShadow = '0 0 8px ' + (red ? 'rgba(255,23,23,.7)' : 'rgba(0,237,239,.7)');
        p.style.animationDuration = (1.6 + Math.random() * 1.8) + 's';
        p.style.animationDelay = (Math.random() * 0.8) + 's';
        document.body.appendChild(p);
        (function (node) { setTimeout(function () { node.remove(); }, 4500); })(p);
      }
      toast('CHEAT ACTIVÉ — <strong>PLUIE</strong> <em>DE CALIBRE</em>', 3000);
    });

    /* 5. It's 9:09 (or 21:09) → a wink from the clock */
    if (!sessionStorage.getItem('nm-egg-909')) {
      var now = new Date();
      if (now.getHours() % 12 === 9 && now.getMinutes() === 9) {
        sessionStorage.setItem('nm-egg-909', '1');
        setTimeout(function () {
          toast('Il est <em>9:09</em>. Ce n’est pas un hasard.', 3600);
        }, 3500);
      }
    }

    /* 6. Reached the very bottom of the page → 9/9 (once per session) */
    if (!sessionStorage.getItem('nm-egg-bottom')) {
      var botT = null;
      window.addEventListener('scroll', function onBottom() {
        clearTimeout(botT);
        botT = setTimeout(function () {
          var doc = document.documentElement;
          if (window.scrollY + window.innerHeight < doc.scrollHeight - 40) return;
          sessionStorage.setItem('nm-egg-bottom', '1');
          window.removeEventListener('scroll', onBottom);
          var barFill = document.querySelector('.nm-scroll-progress');
          if (barFill && window.gsap) {
            gsap.fromTo(barFill, { boxShadow: '0 0 0 rgba(0,237,239,0)' },
              { boxShadow: '0 0 18px rgba(0,237,239,.9)', duration: 0.35, yoyo: true, repeat: 3 });
          }
          toast('Tu es allé au bout. <strong>9</strong>/<em>9</em>.', 3200);
        }, 120);
      }, { passive: true });
    }

    /* 7. MACH 9 — flick the 3D hero can hard (dispatched by 9mm-webgl.js) */
    window.addEventListener('nm:mach9', function () {
      if (sessionStorage.getItem('nm-egg-mach9')) return;
      sessionStorage.setItem('nm-egg-mach9', '1');
      toast('<strong>MACH 9</strong> — vitesse de calibre ⚡', 3000);
    });
  }

  /* ==========================================================
     PAGE TRANSITIONS — exit wipe. Arrival is already covered by
     the branded loader, so we only choreograph the way OUT.
     Styles are injected here (self-contained, no CSS file dep).
     ========================================================== */
  var navigateWithWipe; // shared with the easter egg's delayed logo nav

  function initPageWipe() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      navigateWithWipe = function (href) { window.location.href = href; };
      return;
    }

    var css = document.createElement('style');
    css.textContent =
      '.nm-wipe{position:fixed;inset:0;z-index:11000;pointer-events:none;transform:translateY(101%);' +
      'background:linear-gradient(105deg,rgba(255,23,23,.18),transparent 40%),' +
      'linear-gradient(285deg,rgba(0,237,239,.16),transparent 40%),#080810;will-change:transform;}' +
      '.nm-wipe::after{content:"";position:absolute;top:-2px;left:0;right:0;height:3px;' +
      'background:linear-gradient(90deg,var(--rouge-corps,#ff1717),var(--bleu-esprit,#00edef));' +
      'box-shadow:0 0 24px rgba(0,237,239,.8),0 0 12px rgba(255,23,23,.8);}' +
      '.nm-wipe.is-in{transition:transform .38s cubic-bezier(.76,0,.24,1);transform:translateY(0);pointer-events:all;}';
    document.head.appendChild(css);

    var wipe = el('div', 'nm-wipe');
    wipe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(wipe);

    var leaving = false;
    navigateWithWipe = function (href) {
      if (leaving) return;
      leaving = true;
      wipe.classList.add('is-in');
      setTimeout(function () { window.location.href = href; }, 400);
      // failsafe: if navigation is blocked, free the page
      setTimeout(function () { wipe.classList.remove('is-in'); leaving = false; }, 3200);
    };

    // back/forward cache restore: never come back to a covered page
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { wipe.classList.remove('is-in'); leaving = false; }
    });

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download') || a.closest('.nm-logo')) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      // same page + hash → Lenis anchor handling, not a navigation
      if (url.pathname === window.location.pathname && url.hash) return;
      e.preventDefault();
      navigateWithWipe(url.href);
    });
  }

  /* ==========================================================
     EASTER EGG — 9 clicks on the logo = calibre 9.
     Full-screen energy burst + hidden promo code reveal.
     A lone click still navigates home (after a short beat).
     ========================================================== */
  function initEasterEgg() {
    var logo = document.querySelector('a.nm-logo');
    if (!logo) return;

    var css = document.createElement('style');
    css.textContent =
      '.nm-egg{position:fixed;inset:0;z-index:12000;display:flex;align-items:center;justify-content:center;' +
      'background:radial-gradient(circle at 50% 50%,rgba(8,8,16,.92),rgba(8,8,16,.98));opacity:0;pointer-events:none;transition:opacity .4s ease;}' +
      '.nm-egg.is-on{opacity:1;pointer-events:all;}' +
      '.nm-egg-inner{text-align:center;transform:scale(.86);transition:transform .5s cubic-bezier(.16,1,.3,1);}' +
      '.nm-egg.is-on .nm-egg-inner{transform:scale(1);}' +
      '.nm-egg-cal{font-family:var(--font-display,sans-serif);font-size:clamp(3rem,9vw,6.5rem);line-height:1;' +
      'letter-spacing:.06em;color:#fff;text-shadow:0 0 30px rgba(255,23,23,.75),0 0 60px rgba(0,237,239,.5);margin:0;}' +
      '.nm-egg-sub{font-family:var(--font-body,sans-serif);color:rgba(255,255,255,.72);letter-spacing:.32em;' +
      'text-transform:uppercase;font-size:.8rem;margin:1.1rem 0 2rem;}' +
      '.nm-egg-code{display:inline-block;font-family:var(--font-display,monospace);font-size:1.35rem;letter-spacing:.18em;' +
      'padding:.8rem 1.8rem;border:1px dashed rgba(0,237,239,.65);border-radius:10px;color:#00edef;cursor:pointer;' +
      'background:rgba(0,237,239,.06);transition:background .25s ease,transform .25s ease;}' +
      '.nm-egg-code:hover{background:rgba(0,237,239,.14);transform:translateY(-2px);}' +
      '.nm-egg-hint{margin-top:.9rem;font-size:.72rem;color:rgba(255,255,255,.45);letter-spacing:.1em;}' +
      '.nm-egg-close{position:absolute;top:1.4rem;right:1.6rem;font-size:1.6rem;color:rgba(255,255,255,.6);' +
      'background:none;border:none;cursor:pointer;line-height:1;}' +
      '.nm-egg-p{position:fixed;width:7px;height:7px;border-radius:50%;z-index:12001;pointer-events:none;}' +
      '@keyframes nmEggFlash{0%{opacity:.9}100%{opacity:0}}' +
      '.nm-egg-flash{position:fixed;inset:0;z-index:11999;pointer-events:none;' +
      'background:radial-gradient(circle at 50% 40%,rgba(255,23,23,.5),rgba(0,237,239,.25) 45%,transparent 75%);' +
      'animation:nmEggFlash .9s ease-out forwards;}';
    document.head.appendChild(css);

    var count = 0, timer = null, navTimer = null, fired = false;

    function burst() {
      if (fired) return;
      fired = true;

      var flash = el('div', 'nm-egg-flash');
      document.body.appendChild(flash);
      setTimeout(function () { flash.remove(); }, 1000);

      // particle spray from the logo
      var r = logo.getBoundingClientRect();
      var ox = r.left + r.width / 2, oy = r.top + r.height / 2;
      for (var i = 0; i < 36; i++) {
        var p = el('div', 'nm-egg-p');
        var red = i % 2 === 0;
        p.style.left = ox + 'px'; p.style.top = oy + 'px';
        p.style.background = red ? '#ff1717' : '#00edef';
        p.style.boxShadow = '0 0 10px ' + (red ? '#ff1717' : '#00edef');
        document.body.appendChild(p);
        if (window.gsap) {
          var ang = (i / 36) * Math.PI * 2;
          var d = 120 + (i % 5) * 60;
          gsap.to(p, { x: Math.cos(ang) * d, y: Math.sin(ang) * d, opacity: 0, scale: 0.3,
            duration: 0.9 + (i % 4) * 0.15, ease: 'power2.out',
            onComplete: (function (node) { return function () { node.remove(); }; })(p) });
        } else {
          (function (node) { setTimeout(function () { node.remove(); }, 900); })(p);
        }
      }

      var egg = el('div', 'nm-egg');
      egg.innerHTML =
        '<button class="nm-egg-close" aria-label="Fermer">&times;</button>' +
        '<div class="nm-egg-inner">' +
        '<p class="nm-egg-cal">CALIBRE&nbsp;9</p>' +
        '<p class="nm-egg-sub">Tu as trouvé le neuvième millimètre</p>' +
        '<span class="nm-egg-code" role="button" tabindex="0" data-code="CALIBRE9">CALIBRE9 &mdash; &minus;9%</span>' +
        '<p class="nm-egg-hint">Clique pour copier &middot; valable sur tout le site</p>' +
        '</div>';
      document.body.appendChild(egg);
      requestAnimationFrame(function () { egg.classList.add('is-on'); });

      function close() {
        egg.classList.remove('is-on');
        setTimeout(function () { egg.remove(); fired = false; }, 450);
      }
      egg.addEventListener('click', function (e) {
        var codeEl = e.target.closest ? e.target.closest('.nm-egg-code') : null;
        if (codeEl) {
          if (navigator.clipboard) navigator.clipboard.writeText(codeEl.getAttribute('data-code'));
          codeEl.textContent = 'COPIÉ ✓';
          setTimeout(close, 900);
          return;
        }
        close();
      });
    }

    logo.addEventListener('click', function (e) {
      e.preventDefault();
      clearTimeout(timer); clearTimeout(navTimer);
      count++;
      if (count >= 9) { count = 0; burst(); return; }
      // a lone click still goes home after a short beat
      navTimer = setTimeout(function () {
        if (count === 1 && window.location.pathname !== '/') {
          if (navigateWithWipe) navigateWithWipe('/'); else window.location.href = '/';
        }
        count = 0;
      }, 500);
      timer = setTimeout(function () { count = 0; }, 1600);
    });
  }

  function initCollectionCards() {
    var cards = document.querySelectorAll('.nm-collection-grid .nm-product-card');
    if (!cards.length) return;
    cards.forEach(function (card) {
      var t = (card.querySelector('.nm-product-card-title') || {}).textContent || '';
      card.classList.add(/mind/i.test(t) ? 'nm-card-mind' : 'nm-card-body');
    });
    // quick-add feedback pulse (the theme handles the actual /cart/add)
    document.addEventListener('click', function (e) {
      var qa = e.target.closest && e.target.closest('.nm-quick-add');
      if (!qa) return;
      qa.classList.remove('is-added');
      void qa.offsetWidth;
      qa.classList.add('is-added');
    }, true);
  }

  function initFlyToCart() {
    if (!window.gsap) return;
    var gsap = window.gsap;

    function cartTarget() {
      return document.querySelector('a[href="/cart"].nm-cart-btn') ||
             document.querySelector('a[href="/cart"]');
    }

    function sourceImage(origin) {
      // first VISIBLE image near the trigger (gallery slides can be 0-width),
      // else the page's main product visual
      var scope = origin.closest('form, .nm-product, .nm-split-panel') || document;
      var candidates = Array.prototype.slice.call(scope.querySelectorAll('img'))
        .concat(Array.prototype.slice.call(document.querySelectorAll('.nm-product img, .nm-hero-visual img, main img')));
      for (var i = 0; i < candidates.length; i++) {
        var r = candidates[i].getBoundingClientRect();
        if (r.width > 40 && r.height > 40 && r.bottom > 0 && r.top < window.innerHeight) return candidates[i];
      }
      return null;
    }

    function fly(origin) {
      var cart = cartTarget();
      var img = sourceImage(origin);
      if (!cart || !img) return;
      var from = img.getBoundingClientRect();
      var to = cart.getBoundingClientRect();
      if (!from.width || !to.width) return;

      var ghost = img.cloneNode(false);
      ghost.style.cssText = 'position:fixed;z-index:10500;pointer-events:none;margin:0;' +
        'left:' + from.left + 'px;top:' + from.top + 'px;' +
        'width:' + from.width + 'px;height:' + from.height + 'px;object-fit:contain;';
      document.body.appendChild(ghost);

      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);

      var tl = gsap.timeline({ onComplete: function () { ghost.remove(); } });
      // arc: rise first, then dive to the cart
      tl.to(ghost, { y: -46, scale: 0.82, rotation: -7, duration: 0.28, ease: 'power2.out' })
        .to(ghost, { x: dx, y: dy, scale: 0.06, rotation: 14, autoAlpha: 0.25, duration: 0.65, ease: 'power2.in' });

      // cart badge pulse
      var count = document.querySelector('.nm-cart-count');
      tl.add(function () {
        if (count) gsap.fromTo(count, { scale: 1.9 }, { scale: 1, duration: 0.5, ease: 'elastic.out(1.2, 0.45)' });
        if (cart) gsap.fromTo(cart, { scale: 1.25 }, { scale: 1, duration: 0.4, ease: 'back.out(2)' });
      });
    }

    // AJAX product forms (main form, cross-sell) — capture, don't interfere
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.getAttribute && (f.getAttribute('action') || '').indexOf('/cart/add') === 0) fly(f);
    }, true);

    // Sticky ATC button triggers its own fetch on click
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('#nm-sticky-atc-btn');
      if (btn && !btn.disabled) fly(btn);
    }, true);
  }

  function initLenis() {
    if (reduce || !window.Lenis) return;
    var lenis = new window.Lenis({ lerp: 0.11, wheelMultiplier: 1.0 });
    window.nmLenis = lenis;

    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      (function raf(time) { lenis.raf(time); requestAnimationFrame(raf); })(0);
    }

    // Anchor links: route through Lenis (capture beats the theme's own handler)
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id === '#') return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      lenis.scrollTo(target, { offset: -90, duration: 1.2 });
    }, true);

    // Pause smooth scroll while a fullscreen overlay owns the page
    function overlayOpen() {
      return document.body.classList.contains('nm-menu-open') ||
             document.querySelector('.nm-drawer-overlay.is-open') ||
             document.querySelector('.nm-search-overlay.is-open, .nm-search-overlay.is-visible');
    }
    function syncOverlay() {
      if (overlayOpen()) lenis.stop(); else lenis.start();
    }
    document.addEventListener('click', function () { setTimeout(syncOverlay, 80); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setTimeout(syncOverlay, 80); });
  }

  function initCursorStates() {
    var cursor = document.getElementById('nm-cursor');
    if (!cursor) return;
    // "VOIR" only where it means something: real photos, not product panels
    document.addEventListener('mouseover', function (e) {
      var media = e.target.closest && e.target.closest('.nm-insta-grid a, .nm-insta-grid > *, .nm-story-img-wrap');
      cursor.classList.toggle('nm-cur-view', !!media);
    });
  }

  function initWorldSync() {
    var body = document.body;
    var atmos = document.querySelector('.nm-atmos');
    var hero = document.querySelector('.nm-hero-slider');
    var syncing = false;

    function currentMode() { return body.getAttribute('data-mode') || 'body'; }

    // Dramatic bloom + hero sync whenever the world changes
    function onWorldChange(mode) {
      if (atmos) {
        atmos.classList.remove('is-flip');
        void atmos.offsetWidth; // restart animation
        atmos.classList.add('is-flip');
      }
      // Sync the hero to the chosen world. Clicking the tab also bubbles to the
      // hero section, which clears its 6s auto-rotation — so once the user picks
      // a world, the hero locks to it instead of drifting back.
      if (hero) {
        var tab = hero.querySelector('.nm-hs-tab--' + mode);
        if (tab) { syncing = true; tab.click(); syncing = false; }
      }
    }

    // Observe the existing toggle's data-mode changes
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          if (m.attributeName === 'data-mode') onWorldChange(currentMode());
        });
      });
      mo.observe(body, { attributes: true, attributeFilter: ['data-mode'] });
    }

    // A user clicking a hero tab should flip the whole world too
    if (hero) {
      hero.querySelectorAll('.nm-hs-tab').forEach(function (tab) {
        tab.addEventListener('click', function () {
          if (syncing) return; // ignore programmatic clicks from onWorldChange
          var want = tab.getAttribute('data-product'); // 'body' | 'mind'
          if (want && want !== currentMode()) {
            var toggle = document.getElementById('nm-mode-btn');
            if (toggle) toggle.click();
          }
        });
      });
    }
  }

  // mobile: everything here is decorative enhancement — yield the load
  // window to the browser and boot in idle (TBT on slow CPUs)
  function boot() {
    if (window.matchMedia('(max-width: 768px)').matches) {
      var idle = window.requestIdleCallback || function (f) { setTimeout(f, 400); };
      idle(build, { timeout: 2000 });
    } else {
      build();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
